/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import * as AppPropTypes from '../../lib/PropTypes';

import { useUser } from '../../contexts/AuthContext';
import { useFavorites } from '../../contexts/FavoritesContext';
import { useUrlGenerator } from '../../contexts/RoutesContext';
import AddToCalendarIcon from '../icons/AddToCalendar';
import HeartIcon from '../icons/Heart';
import IconLink from '../partials/IconLink';

import styles from '../../../styles/buttons/add-to-favorites.module.scss';

const propTypes = {
    item: AppPropTypes.listItem,
    onLogin: PropTypes.func,
    loginNext: PropTypes.string,
    className: PropTypes.string,
    withoutIcon: PropTypes.bool,
    withoutLabel: PropTypes.bool,
    isDark: PropTypes.bool,
};

const defaultProps = {
    item: null,
    onLogin: null,
    loginNext: null,
    className: null,
    withoutIcon: false,
    withoutLabel: false,
    isDark: false,
};

const AddToFavorites = ({
    item,
    onLogin,
    loginNext,
    className,
    withoutIcon,
    withoutLabel,
    isDark,
    ...props
}) => {
    const intl = useIntl();
    const { id = null, type = null, url = null } = item || {};
    const route = useUrlGenerator();
    const user = useUser();
    const { addFavorite, removeFavorite, hasFavorite, loading } = useFavorites();
    const favoriteType = type === 'screening' ? 'event' : type;
    const inFavorites = hasFavorite(favoriteType, id);

    const onClick = useCallback(() => {
        if (inFavorites) {
            removeFavorite(favoriteType, id);
        } else {
            addFavorite(favoriteType, id);
        }
    }, [id, inFavorites, addFavorite, removeFavorite, favoriteType]);

    const icon =
        favoriteType === 'event' ? <AddToCalendarIcon isRemove={inFavorites} /> : <HeartIcon />;

    const label =
        // eslint-disable-next-line no-nested-ternary
        favoriteType === 'event' ? (
            !inFavorites ? (
                <FormattedMessage defaultMessage="Add to calendar" description="Button label" />
            ) : (
                <FormattedMessage
                    defaultMessage="Remove from calendar"
                    description="Button label"
                />
            )
        ) : !inFavorites ? (
            <FormattedMessage defaultMessage="Add to favorites" description="Button label" />
        ) : (
            <FormattedMessage defaultMessage="Remove from favorites" description="Button label" />
        );

    const ariaLabel =
        // eslint-disable-next-line no-nested-ternary
        favoriteType === 'event'
            ? !inFavorites
                ? intl.formatMessage({
                      defaultMessage: 'Add to calendar',
                      description: 'Button label',
                  })
                : intl.formatMessage({
                      defaultMessage: 'Remove from calendar',
                      description: 'Button label',
                  })
            : !inFavorites
            ? intl.formatMessage({
                  defaultMessage: 'Add to favorites',
                  description: 'Button label',
              })
            : intl.formatMessage({
                  defaultMessage: 'Remove from favorites',
                  description: 'Button label',
              });

    return (
        <IconLink
            className={classNames([
                styles.container,
                styles[favoriteType],
                {
                    [className]: className !== null,
                    [styles.isAdded]: inFavorites,
                    [styles.isDark]: isDark,
                },
            ])}
            icon={!withoutIcon ? icon : null}
            label={!withoutLabel ? label : null}
            aria-label={ariaLabel}
            onClick={user !== null ? onClick : onLogin}
            loading={loading}
            href={
                user === null
                    ? `${route('login')}?next=${encodeURIComponent(loginNext || url)}&favorites=1`
                    : null
            }
            isDark={isDark}
            {...props}
        />
    );
};
AddToFavorites.propTypes = propTypes;
AddToFavorites.defaultProps = defaultProps;

export default AddToFavorites;
